import { Dropdown, Pivot, PivotItem, TextField, Toggle } from "@fluentui/react";
import { t } from "i18next";

import ConditionBuilder from "./ConditionBuilder";
import { ReadField, readOption, readYesNo } from "./ReadOnly";
import { NO_FIELD_PROPS } from "../../helpers/hangingProtocol/hpFormFields";
import {
  ADVANCE_VIEWER_UI_ENABLED,
  PRIORS_UI_ENABLED,
} from "../../helpers/hangingProtocol/featureFlags";
import { isDeadViewport } from "../../helpers/hangingProtocol/hpDocument";
import {
  ALIGNMENT_OPTIONS,
  MPR_MIST,
  MPR_MIST_OPTIONS,
  MPR_SERIES_IN_VIEWER,
  MPR_SERIES_IN_VIEWER_OPTIONS,
  SCALE,
  SCALE_OPTIONS,
} from "../../helpers/hangingProtocol/protocolEnums";
import "./viewportInspector.css";

/**
 * Everything configurable on one viewport, as a side panel.
 *
 * MedDream stacks all of this inline for every viewport in a single scrolling
 * modal — a 2x2 protocol becomes ~40 controls with no way to compare cells.
 * Here the grid stays visible and only the selected cell's settings show.
 *
 * The three groups are tabs rather than stacked sections: matching is what gets
 * edited on nearly every view, and pairing it with a dozen presentation fields
 * put a scrollbar on the one thing that should never need scrolling. Tabs also
 * keep what the viewport *matches* apart from how it *presents*, and both apart
 * from the features only the Advance Viewer can render.
 *
 * That third tab is behind `ADVANCE_VIEWER_UI_ENABLED` until both viewers can
 * hang those settings, so `showAdvanced` alone no longer opens it — the tab and
 * its fields are otherwise unchanged, waiting on the flag.
 */

/**
 * `windowing` is either null or `{ windowCenter, windowWidth }` — confirmed from
 * MedDream's bundle, since every value in the reference export is null. Blanking
 * both fields must restore null rather than leave an empty object, or the
 * exported file gains a shape MedDream does not write.
 */
function buildWindowing(current, patch) {
  const next = { ...(current || {}), ...patch };
  const width = next.windowWidth;
  const center = next.windowCenter;
  const isBlank = (v) => v === "" || v === null || v === undefined;
  if (isBlank(width) && isBlank(center)) return null;
  return {
    windowCenter: isBlank(center) ? null : Number(center),
    windowWidth: isBlank(width) ? null : Number(width),
  };
}

/** Numbers that are optional in the format: "" means unset, not zero. */
function toOptionalNumber(text) {
  return text === "" || text === undefined || text === null ? null : Number(text);
}

/** One tab's body. `note` qualifies the whole group, so it leads. */
function Section({ children, note }) {
  return (
    <section className="hpv2-inspector-section">
      {note && <p className="hpv2-inspector-note">{note}</p>}
      {children}
    </section>
  );
}

/**
 * `field` names this viewport's boxes relative to the viewport itself —
 * `field("rotation")`, `field("windowing.windowWidth")`. The layout step anchors
 * those to wherever the viewport sits in the document, so the inspector stays
 * ignorant of its own coordinates. Optional: a viewport rendered outside a form
 * gets the no-op and behaves as it always did.
 */
export default function ViewportInspector({
  viewport,
  onChange,
  disabled = false,
  showAdvanced = false,
  field = NO_FIELD_PROPS,
}) {
  if (!viewport) {
    return <div className="hpv2-inspector hpv2-inspector-empty">{t("hanging-protocol.no-viewport-selected")}</div>;
  }

  const set = (patch) => onChange({ ...viewport, ...patch });
  const windowing = viewport.windowing || {};

  return (
    <div className="hpv2-inspector">
      <header className="hpv2-inspector-header">
        <span className="hpv2-inspector-title">
          {t("hanging-protocol.viewport")} {viewport.row}×{viewport.column}
        </span>
      </header>

      {/* Uncontrolled on purpose: clicking through views should keep the tab
          you were working in rather than snap back to matching. */}
      <Pivot className="hpv2-inspector-pivot" overflowBehavior="menu">
        <PivotItem itemKey="matching" headerText={t("hanging-protocol.matching")}>
          <Section>
            <ConditionBuilder
              slot="seriesConditions"
              label={t("hanging-protocol.series-conditions")}
              value={viewport.seriesConditions}
              disabled={disabled}
              onChange={(next) => set({ seriesConditions: next })}
            />
            <ConditionBuilder
              slot="instanceConditions"
              label={t("hanging-protocol.instance-conditions")}
              value={viewport.instanceConditions}
              disabled={disabled}
              onChange={(next) => set({ instanceConditions: next })}
            />
            {isDeadViewport(viewport) && (
              <p className="hpv2-inspector-warning" role="alert">
                {t("hanging-protocol.no-instance-condition-help")}
              </p>
            )}
          </Section>
        </PivotItem>

        <PivotItem itemKey="presentation" headerText={t("hanging-protocol.presentation")}>
          <Section>
            {/* Read as a list of settings, written as a panel of controls. Nine
                greyed-out boxes down the side of the grid was the worst of it: a
                cell that sets nothing looked exactly like one that sets
                everything, because empty and disabled render the same. */}
            {disabled ? (
              <>
                <div className="hpv2-inspector-row">
                  <ReadField
                    label={t("hanging-protocol.window-width")}
                    value={windowing.windowWidth ?? null}
                  />
                  <ReadField
                    label={t("hanging-protocol.window-center")}
                    value={windowing.windowCenter ?? null}
                  />
                  <ReadField
                    label={t("hanging-protocol.rotation")}
                    value={String(viewport.rotation ?? 0)}
                  />
                </div>

                <div className="hpv2-inspector-row">
                  <ReadField
                    label={t("hanging-protocol.horizontal-flip")}
                    value={readYesNo(viewport.flipHorizontally)}
                  />
                  <ReadField
                    label={t("hanging-protocol.vertical-flip")}
                    value={readYesNo(viewport.flipVertically)}
                  />
                </div>

                <div className="hpv2-inspector-row">
                  <ReadField
                    label={t("hanging-protocol.scale")}
                    value={readOption(SCALE_OPTIONS, viewport.scale ?? SCALE.FIT_TO_SCREEN)}
                  />
                  {viewport.scale === SCALE.ZOOM && (
                    <ReadField
                      label={t("hanging-protocol.custom-scale")}
                      value={String(viewport.customScaleValue ?? 1)}
                    />
                  )}
                  <ReadField
                    label={t("hanging-protocol.alignment")}
                    value={readOption(ALIGNMENT_OPTIONS, viewport.alignment)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="hpv2-inspector-row">
                  <TextField
                    label={t("hanging-protocol.window-width")}
                    type="number"
                    value={windowing.windowWidth ?? ""}
                    onChange={(_e, next) => set({ windowing: buildWindowing(viewport.windowing, { windowWidth: next }) })}
                    {...field("windowing.windowWidth")}
                  />
                  <TextField
                    label={t("hanging-protocol.window-center")}
                    type="number"
                    value={windowing.windowCenter ?? ""}
                    onChange={(_e, next) => set({ windowing: buildWindowing(viewport.windowing, { windowCenter: next }) })}
                    {...field("windowing.windowCenter")}
                  />
                  <TextField
                    label={t("hanging-protocol.rotation")}
                    type="number"
                    value={String(viewport.rotation ?? 0)}
                    onChange={(_e, next) => set({ rotation: Number(next) || 0 })}
                    {...field("rotation")}
                  />
                </div>

                <div className="hpv2-inspector-row">
                  <Toggle
                    label={t("hanging-protocol.horizontal-flip")}
                    checked={Boolean(viewport.flipHorizontally)}
                    onChange={(_e, checked) => set({ flipHorizontally: Boolean(checked) })}
                  />
                  <Toggle
                    label={t("hanging-protocol.vertical-flip")}
                    checked={Boolean(viewport.flipVertically)}
                    onChange={(_e, checked) => set({ flipVertically: Boolean(checked) })}
                  />
                </div>

                <div className="hpv2-inspector-row">
                  <Dropdown
                    label={t("hanging-protocol.scale")}
                    selectedKey={viewport.scale ?? SCALE.FIT_TO_SCREEN}
                    options={SCALE_OPTIONS.map((o) => ({ key: o.value, text: o.label }))}
                    onChange={(_e, option) => set({ scale: option.key })}
                  />
                  {/* Only meaningful for the Zoom scale mode. */}
                  {viewport.scale === SCALE.ZOOM && (
                    <TextField
                      label={t("hanging-protocol.custom-scale")}
                      type="number"
                      value={String(viewport.customScaleValue ?? 1)}
                      onChange={(_e, next) => set({ customScaleValue: Number(next) || 1 })}
                      {...field("customScaleValue")}
                    />
                  )}
                  <Dropdown
                    label={t("hanging-protocol.alignment")}
                    selectedKey={viewport.alignment}
                    options={ALIGNMENT_OPTIONS.map((o) => ({ key: o.value, text: o.label }))}
                    onChange={(_e, option) => set({ alignment: option.key })}
                  />
                </div>
              </>
            )}

            {/* Which prior / other study this cell hangs — hidden with the rest
                of prior authoring until PRIORS_UI_ENABLED. */}
            {PRIORS_UI_ENABLED && (
              <div className="hpv2-inspector-row">
                {disabled ? (
                  <>
                    <ReadField
                      label={t("hanging-protocol.prior-study-number")}
                      value={viewport.priorStudy ?? null}
                    />
                    <ReadField
                      label={t("hanging-protocol.other-study-number")}
                      value={viewport.otherStudy ?? null}
                    />
                  </>
                ) : (
                  <>
                    <TextField
                      label={t("hanging-protocol.prior-study-number")}
                      type="number"
                      value={viewport.priorStudy ?? ""}
                      onChange={(_e, next) => set({ priorStudy: toOptionalNumber(next) })}
                      {...field("priorStudy")}
                    />
                    <TextField
                      label={t("hanging-protocol.other-study-number")}
                      type="number"
                      value={viewport.otherStudy ?? ""}
                      onChange={(_e, next) => set({ otherStudy: toOptionalNumber(next) })}
                      {...field("otherStudy")}
                    />
                  </>
                )}
              </div>
            )}
          </Section>
        </PivotItem>

        {/* Held back with the rest of the Advance-Viewer-only settings until
            both viewers can hang them (ADVANCE_VIEWER_UI_ENABLED). */}
        {showAdvanced && ADVANCE_VIEWER_UI_ENABLED ? (
          <PivotItem itemKey="advanced" headerText={t("hanging-protocol.advance-only")}>
            <Section note={t("hanging-protocol.advance-only-note")}>
              <div className="hpv2-inspector-row">
                {disabled ? (
                  <>
                    <ReadField
                      label={t("hanging-protocol.mpr-mist")}
                      value={readOption(MPR_MIST_OPTIONS, viewport.mprMist ?? MPR_MIST.DISABLED)}
                    />
                    <ReadField
                      label={t("hanging-protocol.mpr-series-in-viewer")}
                      value={readOption(
                        MPR_SERIES_IN_VIEWER_OPTIONS,
                        viewport.mprSeriesInViewer ?? MPR_SERIES_IN_VIEWER.NO
                      )}
                    />
                  </>
                ) : (
                  <>
                    <Dropdown
                      label={t("hanging-protocol.mpr-mist")}
                      selectedKey={viewport.mprMist ?? MPR_MIST.DISABLED}
                      options={MPR_MIST_OPTIONS.map((o) => ({ key: o.value, text: o.label }))}
                      onChange={(_e, option) => set({ mprMist: option.key })}
                    />
                    <Dropdown
                      label={t("hanging-protocol.mpr-series-in-viewer")}
                      selectedKey={viewport.mprSeriesInViewer ?? MPR_SERIES_IN_VIEWER.NO}
                      options={MPR_SERIES_IN_VIEWER_OPTIONS.map((o) => ({ key: o.value, text: o.label }))}
                      onChange={(_e, option) => set({ mprSeriesInViewer: option.key })}
                    />
                  </>
                )}
              </div>

              {disabled ? (
                <ReadField
                  label={t("hanging-protocol.adjust-layout")}
                  value={readYesNo(viewport.adjustLayout)}
                />
              ) : (
                <Toggle
                  label={t("hanging-protocol.adjust-layout")}
                  checked={Boolean(viewport.adjustLayout)}
                  onChange={(_e, checked) => set({ adjustLayout: Boolean(checked) })}
                />
              )}

              <ConditionBuilder
                slot="petSeriesConditions"
                label={t("hanging-protocol.pet-series-conditions")}
                value={viewport.petSeriesConditions}
                disabled={disabled}
                onChange={(next) => set({ petSeriesConditions: next })}
              />
              <ConditionBuilder
                slot="koInstance"
                label={t("hanging-protocol.ko-instance")}
                value={viewport.koInstance}
                disabled={disabled}
                onChange={(next) => set({ koInstance: next })}
              />
              <ConditionBuilder
                slot="autoApplyPR"
                label={t("hanging-protocol.auto-apply-pr")}
                value={viewport.autoApplyPR}
                disabled={disabled}
                onChange={(next) => set({ autoApplyPR: next })}
              />
            </Section>
          </PivotItem>
        ) : null}
      </Pivot>
    </div>
  );
}
